import React, { useState, useEffect } from 'react';
import { Search, MapPin, Calendar, Eye, DollarSign, Clock, Star } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

export default function ClassifiedInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClassifiedInquiries();
  }, []);

  const fetchClassifiedInquiries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/classified-inquiries`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data.inquiries || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading classified inquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classified-inquiries">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Classified Patient Inquiries</h1>
          <p className="text-gray-600 mt-1">Browse published patient treatment requests</p>
        </div>
        <Badge className="bg-green-100 text-green-800 px-4 py-2 text-lg">
          {inquiries.length} Available Inquiries
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Inquiries</p>
                <p className="text-2xl font-bold text-gray-900">{inquiries.length}</p>
              </div>
              <Search className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-gray-900">
                  {inquiries.filter(i => i.priority === 'high' || i.priority === 'urgent').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Countries</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(inquiries.map(i => i.patient_country).filter(Boolean)).size}
                </p>
              </div>
              <MapPin className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Treatment Types</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(inquiries.map(i => i.treatment_type).filter(Boolean)).size}
                </p>
              </div>
              <Star className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {inquiries.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Inquiries Found</h3>
            <p className="text-gray-500">No classified inquiries available.</p>
          </div>
        ) : (
          inquiries.map((inquiry) => (
            <Card key={inquiry._id} className="hover:shadow-lg transition-shadow border-2 hover:border-blue-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{inquiry.patient_name}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {inquiry.patient_country}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={
                        inquiry.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                        inquiry.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        inquiry.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {inquiry.priority}
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-800">
                        {inquiry.treatment_type}
                      </Badge>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Condition Description</h4>
                    <p className="text-sm text-gray-600 line-clamp-3">{inquiry.condition_description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Age:</span>
                      <span className="ml-2 text-gray-600">{inquiry.patient_age || 'Not specified'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Gender:</span>
                      <span className="ml-2 text-gray-600">{inquiry.patient_gender || 'Not specified'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm border-t pt-4">
                    <div className="flex items-center gap-1 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Budget: {inquiry.budget_range || 'Open'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(inquiry.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid={`contact-patient-${inquiry._id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Full Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {inquiries.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {inquiries.length} classified inquiries
        </div>
      )}
    </div>
  );
}